import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorAlert } from '../../common/views/ErrorAlert/ErrorAlert';
import { MarkReleasedUrlRow } from './MarkReleasedUrlRow';
import { usePresaveReleaseUrls } from './usePresaveReleaseUrls';
import { useReleaseCampaign } from './useReleaseCampaign';

interface MarkReleasedModalProps {
  isOpen: boolean;
  closeModal: () => void;
  accountId: number;
  campaignId: number;
  campaignUuid?: string;
  smartLinkId: number;
}

/**
 * The release itself. One link is enough to start: musicapi identifies it and
 * finds the same track on the other services, and the artist corrects the list
 * before it goes out. Nothing is saved until they confirm.
 */
const MarkReleasedModalComponent: React.FC<MarkReleasedModalProps> = ({
  isOpen,
  closeModal,
  accountId,
  campaignId,
  campaignUuid,
  smartLinkId,
}) => {
  const intl = useIntl();
  const [pastedUrl, setPastedUrl] = useState('');
  const {
    items,
    prefill,
    changeUrl,
    removeUrl,
    addUrl,
    clear,
    isLoading: isPrefillLoading,
    error: prefillError,
  } = usePresaveReleaseUrls();
  const {
    release,
    isLoading: isReleaseLoading,
    error: releaseError,
  } = useReleaseCampaign({ accountId, campaignId, campaignUuid, smartLinkId });

  const onPastedUrlChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPastedUrl(event.target.value);
  }, []);

  const onFindLinks = useCallback(() => {
    prefill(pastedUrl.trim()).catch(console.error);
  }, [pastedUrl, prefill]);

  const onCancel = useCallback(() => {
    clear();
    setPastedUrl('');
    closeModal();
  }, [clear, closeModal]);

  const urls = useMemo(() => items.map((item) => item.url.trim()).filter((url) => url !== ''), [items]);

  const onConfirm = useCallback(() => {
    release(urls)
      .then((released) => {
        if (!released) {
          return;
        }
        clear();
        setPastedUrl('');
        closeModal();
      })
      .catch(console.error);
  }, [clear, closeModal, release, urls]);

  const isLoading = isPrefillLoading || isReleaseLoading;
  const hasLinks = items.length > 0;
  // A rejected release comes back as a 400 naming every url it could not use,
  // and `ErrorAlert` prints an operational error's own message.
  const error = prefillError ?? releaseError;

  return (
    <Modal isOpen={isOpen} toggle={onCancel} centered size="lg" className="sml-modal">
      <ModalHeader toggle={onCancel}>
        {intl.formatMessage({ id: 'app.sml.presave.release.title_v2', defaultMessage: 'My song is out now' })}
      </ModalHeader>
      <ModalBody>
        {error !== undefined && <ErrorAlert error={error} />}
        <p className="opacity-56">
          {intl.formatMessage({
            id: 'app.sml.presave.release.help_v2',
            defaultMessage:
              'This does not send your song to any service. Paste one link to the song on a service where it is out now. We find it on the other services, put the play buttons on this page and save the song for every fan who pre-saved it.',
          })}
        </p>
        <Input
          type="text"
          value={pastedUrl}
          disabled={isLoading}
          onChange={onPastedUrlChange}
          placeholder={intl.formatMessage({
            id: 'app.sml.presave.release.url_placeholder',
            defaultMessage: 'Enter or paste the release URL',
          })}
        />
        <div className="mt-2 mb-3">
          <SMLButton color="secondary" onClick={onFindLinks} disabled={isLoading || pastedUrl.trim() === ''}>
            {intl.formatMessage({ id: 'app.sml.presave.release.find_links', defaultMessage: 'Find the other links' })}
          </SMLButton>
        </div>
        {isLoading && <LoadingView />}
        {hasLinks && (
          <>
            {items.map((item) => (
              <MarkReleasedUrlRow
                key={item.id}
                item={item}
                disabled={isLoading}
                onChange={changeUrl}
                onRemove={removeUrl}
              />
            ))}
            <SMLButton color="link" onClick={addUrl} disabled={isLoading}>
              {intl.formatMessage({ id: 'app.sml.presave.release.add_url', defaultMessage: 'Add another link' })}
            </SMLButton>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <SMLButton outline onClick={onCancel} disabled={isLoading}>
          {intl.formatMessage({ id: 'app.forms.buttons.cancel.label', defaultMessage: 'Cancel' })}
        </SMLButton>
        <SMLButton color="primary" omitCap onClick={onConfirm} disabled={isLoading || urls.length === 0}>
          {intl.formatMessage({
            id: 'app.sml.presave.release.confirm_v2',
            defaultMessage: 'Update the page and save for fans',
          })}
        </SMLButton>
      </ModalFooter>
    </Modal>
  );
};

export const MarkReleasedModal = TypedMemo(MarkReleasedModalComponent);
